"use client";
/**
 * Requirements Summary:
 * - Generic form dropdown that portal-renders its menu so it escapes overflow
 *   clipping inside modals. PR #459 bumped the portal z-index above the modal
 *   stack (UnifiedAthleteModal, EvaluationModal sit at 1000001) to fix
 *   dropdowns rendering behind their owning modal. A central z-index token
 *   layer is the long-term fix; tracked as TODO in useAnchoredMenuPosition.
 * - The rect maths, upward flip, and scroll/resize tracking that used to live
 *   here now live in `useAnchoredMenuPosition`, shared with the other menus
 *   that had to portal for the same reason.
 */
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { useAnchoredMenuPosition, ANCHORED_MENU_Z_INDEX } from '@/hooks/ui/useAnchoredMenuPosition';

export interface DropdownOption {
  value: string;
  label: string;
  description?: string; // Optional helper text shown beneath the label in the option list
  icon?: string; // Optional Google Material Icon name
  iconColor?: string; // Optional icon color class (e.g., 'text-red-500')
  disabled?: boolean; // Option stays visible but cannot be selected
}

interface DropdownProps {
  id: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Id of a visible label. The button then reads as "<label> <choice>"
   *  instead of only the current choice. */
  ariaLabelledBy?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = "Select an option",
  required = false,
  error,
  disabled = false,
  size = 'md',
  className = '',
  ariaLabelledBy,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalDropdownRef = useRef<HTMLDivElement>(null);
  const portalMenuRef = useRef<HTMLUListElement>(null);

  // Get the selected option for displaying label and icon
  const selectedOption = options.find(option => option.value === value);
  const selectedLabel = selectedOption?.label || placeholder;

  // Close the dropdown
  const closeDropdown = () => {
    setIsOpen(false);
  };

  const { position: dropdownPosition, recalculate: calculatePosition } = useAnchoredMenuPosition({
    anchorRef: buttonRef,
    open: isOpen,
    // Row height times option count, capped — enough to tell whether the menu fits below.
    // Options with a description render two lines, so they need a taller row and a cap
    // that matches maxHeight, or the open-above/open-below flip underestimates the menu.
    estimatedHeight: options.some(option => option.description)
      ? Math.min(options.length * 62 + 16, 320)
      : Math.min(options.length * 42 + 16, 248),
    maxHeight: 320,
    onDismiss: closeDropdown,
  });

  // Toggle the dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        calculatePosition(); // Always calculate position before opening
      }
      setIsOpen(!isOpen);
    }
  };

  // Handle clicking outside of the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is inside the dropdown button
      if (buttonRef.current?.contains(target)) {
        return;
      }
      
      // Check if click is inside the normal dropdown (not in modal)
      if (dropdownRef.current?.contains(target)) {
        return;
      }
      
      // Check if click is inside the portal dropdown (in modal)
      if (portalDropdownRef.current?.contains(target)) {
        return;
      }
      
      closeDropdown();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Reset scroll to top when dropdown opens so the first option (e.g. Tumbling) is always visible.
  // Previously we scrolled to the selected item, which hid items above it and confused users.
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const menuElement = portalMenuRef.current;
        if (menuElement) {
          menuElement.scrollTop = 0;
        }
      }, 10);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Repositioning on scroll and resize, and closing when the button scrolls out of
  // view, are handled by useAnchoredMenuPosition.

  // Handle selecting an option
  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    closeDropdown();
  };

  // Size-specific classes
  const sizeClasses = {
    sm: 'h-8 px-2 py-1 text-xs pr-8',
    md: 'h-11 px-4 py-2.5 text-sm pr-11',
    lg: 'h-14 px-5 py-3 text-base pr-12'
  };

  // Generate appropriate select classes to match the original Select component
  let selectClasses = `w-full appearance-none rounded-lg border shadow-sm placeholder:text-gray-400 focus:outline-hidden focus:ring-3 ${sizeClasses[size]}`;
  
  // Add conditional styling based on error and disabled states
  if (disabled) {
    selectClasses += ` bg-gray-100 text-gray-500 border-gray-300 opacity-40 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700`;
  } else if (error) {
    selectClasses += ` bg-white border-error-500 focus:border-error-300 focus:ring-error-500/20 dark:border-error-500 dark:bg-gray-900 dark:text-white/90 dark:focus:border-error-800`;
  } else {
    selectClasses += ` bg-white border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800`;
  }

  // Add text color class based on whether there's a selected value
  if (value && value !== "") {
    selectClasses += " text-gray-800 dark:text-white/90";
  } else {
    selectClasses += " text-gray-400 dark:text-gray-400";
  }

  // Portal-rendered dropdown menu. There is deliberately no in-place variant:
  // an inline `absolute` menu is clipped by any scrolling ancestor, which is the
  // bug this component's portal exists to avoid.
  const PortalDropdownMenu = () => (
    <div 
      ref={portalDropdownRef}
      className="fixed flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg"
      // Spread whole — the hook sets top OR bottom and applying only one of them
      // would park the menu in the corner. See AnchoredMenuStyle. maxHeight lands on
      // the panel, so the panel caps and the list scrolls inside it (flex-1 + min-h-0
      // below); box-sizing is border-box app-wide, so the border is inside the cap.
      style={{
        ...dropdownPosition.style,
        zIndex: ANCHORED_MENU_Z_INDEX,
        position: 'fixed' // Ensure fixed positioning
      }}
    >
      <ul
        ref={portalMenuRef}
        className="py-1 text-sm flex-1 min-h-0 overflow-auto scroll-smooth"
        role="listbox"
        // Screen readers do not follow a second aria-labelledby hop, so point
        // the list at the visible label directly when there is one.
        aria-labelledby={ariaLabelledBy ?? `${id}-button`}
        onScroll={(e) => e.stopPropagation()} // Prevent scroll events from bubbling up
      >
        {options.length === 0 ? (
          <li className="px-4 py-2.5 text-gray-500 dark:text-gray-400">
            No options available
          </li>
        ) : (
          options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                className={`flex w-full items-center gap-2 px-4 py-2.5 text-left ${
                  option.disabled
                    ? "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                    : value === option.value
                    ? "bg-blue-100 text-blue-900 dark:bg-blue-800 dark:text-blue-100 font-semibold border-l-4 border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                    : "text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (option.disabled) return;
                  handleSelect(option.value);
                }}
                disabled={option.disabled}
                role="option"
                aria-selected={value === option.value}
                aria-disabled={option.disabled || undefined}
                title={option.label}
              >
                {option.icon && (
                  <GoogleMaterialIcon name={option.icon} size="xs" className={option.iconColor || 'text-gray-500'} />
                )}
                <span className="flex-1 break-words whitespace-normal">
                  <span className="block">{option.label}</span>
                  {option.description && (
                    <span className="block text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                      {option.description}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Hidden actual select for form submission */}
      <select 
        name={name}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
        required={required}
        aria-hidden="true"
        tabIndex={-1}
        disabled={disabled}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map(option => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      
      {/* Custom dropdown button styled to match the original Select component */}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className={`dropdown-toggle ${selectClasses}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby={ariaLabelledBy ? `${ariaLabelledBy} ${id}-button` : id}
        id={`${id}-button`}
        disabled={disabled}
        title={selectedLabel} // Show full text on hover
      >
        <span className="flex items-center gap-2 truncate">
          {selectedOption?.icon && (
            <GoogleMaterialIcon name={selectedOption.icon} size="xs" className={selectedOption.iconColor || 'text-gray-500'} />
          )}
          {selectedLabel}
        </span>
      </button>
      
      {/* Chevron — transform-based centering so it stays aligned regardless of button height (incl. dropdown-compact) */}
      <span className="absolute top-1/2 right-3 -translate-y-1/2 flex items-center justify-center pointer-events-none text-gray-500 dark:text-gray-400">
        <GoogleMaterialIcon name="keyboard_arrow_down" size="xs" />
      </span>
      
      {/* Dropdown menu - force portal rendering to escape stacking context issues */}
      {isOpen && typeof document !== 'undefined' && (
        createPortal(<PortalDropdownMenu />, document.body)
      )}
      
      {/* Error message */}
      {error && (
        <p className="mt-1.5 text-xs text-error-500 dark:text-error-400">{error}</p>
      )}
    </div>
  );
};

export default Dropdown; 